import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import SharedNavbar from '@/components/feature/SharedNavbar';
import SharedFooter from '@/components/feature/SharedFooter';
import NotFound from '@/pages/NotFound';
import { Spinner } from '@/components/ui/EmptyState';
import { supabase } from '@/lib/supabaseClient';
import type { Block, CustomPage } from '@/types/customPage';

function BlockRenderer({ block }: { block: Block }) {
  switch (block.type) {
    case 'heading':
      return (
        <h2
          className={`font-serif font-bold text-[#2A2A2A] ${block.size === 'xl' ? 'text-3xl md:text-5xl' : 'text-2xl md:text-3xl'}`}
        >
          {block.text}
        </h2>
      );
    case 'paragraph':
      return <p className="text-sm md:text-base text-[#5A5A5A] font-light leading-[1.9] whitespace-pre-line">{block.text}</p>;
    case 'image':
      return (
        <figure>
          <img src={block.url} alt={block.alt} className="w-full h-auto rounded-2xl" />
          {block.caption && <figcaption className="text-xs text-[#8A8A8A] mt-2 text-center">{block.caption}</figcaption>}
        </figure>
      );
    case 'button':
      return (
        <a
          href={block.href}
          className="inline-block px-8 py-3.5 bg-[#7B1113] text-white text-xs tracking-[0.15em] uppercase rounded-full hover:bg-[#5C0D0F] transition-colors cursor-pointer text-center"
        >
          {block.label}
        </a>
      );
    default:
      return null;
  }
}

export default function CustomPageRenderer() {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState<CustomPage | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    let mounted = true;
    supabase
      .from('rxk_custom_pages')
      .select('id, slug, title, status, blocks, seo_description')
      .eq('slug', slug)
      .eq('status', 'published')
      .maybeSingle()
      .then(({ data }) => {
        if (mounted) {
          setPage((data as CustomPage) ?? null);
          setLoading(false);
        }
      });
    return () => {
      mounted = false;
    };
  }, [slug]);

  if (loading) {
    return (
      <>
        <SharedNavbar />
        <div className="min-h-screen flex items-center justify-center pt-24">
          <Spinner />
        </div>
        <SharedFooter />
      </>
    );
  }

  if (!page) return <NotFound />;

  return (
    <>
      <title>{`${page.title} | Rx.K Seoul`}</title>
      {page.seo_description && <meta name="description" content={page.seo_description} />}
      <SharedNavbar />
      <main className="w-full min-h-screen bg-white pt-24 pb-20 px-6 md:px-12 lg:px-20">
        <div className="max-w-3xl mx-auto flex flex-col gap-8">
          {page.blocks.map((block, i) => (
            <BlockRenderer key={i} block={block} />
          ))}
        </div>
      </main>
      <SharedFooter />
    </>
  );
}
